import { useEffect, useMemo, useState } from 'react'
import {
  GridColDef,
  GridColumnVisibilityModel,
  useGridApiRef,
} from '@microservices/wiskey-react-table-component'

import { useColumns } from '@pages/ConfiguredEntity/hooks/useColumns'
import { useSetObjectDataOnVariableChange } from '@pages/ConfiguredEntity/hooks/useSubscribeToVariableChange'

import { useFetchAllObjectQuery, useFetchPreFillObjectDataByViewIdQuery } from '@redux/api'

import { useChangeVariableActions, useContextMenu, useKeyPress, useRestrictions } from '@hooks'
import { applyUnsavedChanges, getLocalStorageContext } from '@helpers'
import {
  ACTION_CODE,
  ENTITY,
  EVENT_CODE,
  PASSED_FORM_DATA_VARIANT,
  ROUTES,
  SEARCH_RULE_EXCEPTION,
  SEARCH_RULE_TYPE,
  SIZE_PAGE,
} from '@constants'
import { ActionRow, ConfiguredColumn, ObjectDataRecord, RestrictionData } from '@types'

import useModifyDataAfterLoad from '../../../hooks/actions/useModifyDataAfterLoad'
import { hasRedifinitionByDataType, REDIFINITION_DATA_TYPE } from '../helpers'
import { getSortByColumns } from '../helpers/getSortByColumns'

import { useConfiguredViewParams } from './useConfiguredEntityTypes'
import { useContextMenuHandlers } from './useContextMenuHandlers'
import { useGetActions } from './useGetActions'
import { useHandleColumnDrag } from './useHandleColumnDrag'
import { useHandlers } from './useHandlers'
import { useHandleTooltip } from './useHandleTooltip'

export const useConfiguredEntity = ({
  hasSearchAssistant,
  entityCode,
  outerData,
  outerEventBus,
  entityId,
  path,
  isNestedView,
  parentFormIsDirty,
  type,
  getLeaveForm,
  onResetEditForm,
  onSelectPickerOption,
  parentFormObjectId,
  parentFormObjectCode,
  parentFormElementId,
  parentFormObjectIdType,
  fieldId,
  columnId,
  generalFilterField,
  valueId,
  selectedPickerOptionValue,
  onMultiSelectPickerOption,
  multipleSelectPickerOption,
  objectValueNameOption,
  handleOpenForm,
  search,
  pathname,
  dialogId,
  isListControlDialogWindow,
  customPageSize,
  entity,
  onSetEntity,
  readonly,
  isViewDialogWindow,
  defaultFilter,
  outerRequestData,
  showQuickSearch,
  expanded,
  prefillCreateObjectRecordInForm,
  onRowClick,
  onRowSelection,
  onSortChange,
  draggableRowImageRef,
  initialRestrictions,
  initialCommonParameters,
  globalColumnTextStyles,
  globalColumnHeaderStyles,
  instanceId,
}: useConfiguredViewParams) => {
  const context = getLocalStorageContext()

  const isAssistiantSearch = path === ROUTES.SEARCH && !isNestedView
  const isListControl = type === ENTITY.LIST_CONTROL

  const [visibleColumns, setVisibleColumns] = useState<ConfiguredColumn[]>([])
  const [enrichedObjectData, setEnrichedObjectData] = useState<ObjectDataRecord[]>([])

  const { data: objects } = useFetchAllObjectQuery(undefined, { skip: !isAssistiantSearch })

  const { data: prefillData, isLoading: isPrefillDataLoading } =
    useFetchPreFillObjectDataByViewIdQuery(
      {
        entityId: entity?.id as string,
        objectCode: parentFormObjectCode,
        objectId: parentFormObjectId,
      },
      { skip: !entity?.id }
    )

  const {
    restrictions: entityRestrictions,
    updateRestrictions,
    isLoadingRestrictions,
    removeRestrictions,
    removeAllRestrictions,
  } = useRestrictions(entityId as string, false, initialRestrictions)

  const {
    restrictions: defaultEntityRestrictions,
    updateRestrictions: updateDefaultRestrictions,
    isLoadingRestrictions: isLoadingDefaultRestrictions,
    getRestrictions: fetchDefaultEntityRestrictions,
  } = useRestrictions(entityId as string, true)

  const [isDropDownWindowOpen, setDropDownWindowOpen] = useState(false)

  const viewColumns = useMemo(() => {
    const columns = entity?.columns || []

    return columns
      .filter(col => col.visibility)
      .map((col, index) => {
        const width = entityRestrictions?.width?.find(
          restriction => restriction.fieldCode === col.code
        )?.value
        const order = entityRestrictions?.order?.find(
          restriction => restriction.fieldCode === col.code
        )?.value
        const sort = entityRestrictions?.sort?.find(
          restriction => restriction.fieldCode === col.code
        )?.value
        const show = entityRestrictions?.show?.find(
          restriction => restriction.fieldCode === col.code
        )?.value
        const quickSearchChoice = entityRestrictions?.quickSearchChoice?.find(
          restriction => restriction.fieldCode === col.code
        )?.value
        const quickSearchPermission = entityRestrictions?.quickSearchPermission?.find(
          restriction => restriction.fieldCode === col.code
        )?.value

        return {
          ...col,
          restrictions: {
            order: Number(order || index),
            width,
            sort,
            show,
            quickSearchChoice,
            quickSearchPermission,
          },
        }
      })
      .sort((a, b) => a.restrictions.order - b.restrictions.order)
  }, [entity, entityRestrictions])
  const actions = entity?.actions

  // В dropdown window быстрый поиск есть всегда
  const hasQuickSearch =
    (entity?.hasQuickSearch || type === ENTITY.DROP_DOWN_ENTITY) && showQuickSearch
  // const hasQuickSearch = entity?.hasQuickSearch
  const objectCode = entity?.objectCode || ''

  const apiRef = useGridApiRef()

  const [columns, setColumns] = useState<GridColDef<ObjectDataRecord>[]>([])
  const [initialColumnVisibilityModel, setInitialColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({})

  const handleSetColumnsVisibilityModel = (value: GridColumnVisibilityModel) =>
    setColumnVisibilityModel(value)

  const { keyPressed: isCtrlPressed } = useKeyPress('ControlLeft')

  useEffect(() => {
    if (entityId) {
      fetchDefaultEntityRestrictions(entityId)

      if (isAssistiantSearch && entity) {
        const viewColumns = entity.columns?.filter(col => col.visibility) || []
        const pageSizeForRequest = customPageSize * 2
        const sizeParameter = Number(initialCommonParameters?.pagination)

        refreshObjectData(
          {
            code: entity.objectCode,
            viewId: entity.id,
            size:
              pageSizeForRequest > sizeParameter
                ? pageSizeForRequest.toString()
                : sizeParameter || SIZE_PAGE,
            body: searchAssistantValue,
            sortKeysForQuickSearch: undefined,
            searchValue: undefined,
            searchRule: undefined,
            sort: getSortByColumns(
              viewColumns.map(col => {
                const sort = entityRestrictions?.sort
                  ? entityRestrictions.sort.find(restriction => restriction.fieldCode === col.code)
                      ?.value
                  : undefined

                return { ...col, restrictions: { sort } } as ConfiguredColumn
              })
            ),
          },
          false
        )
      }
    }
  }, [entityId])

  const { state, data, handlers } = useHandlers({
    hasSearchAssistant,
    entityCode,
    entityId,
    path,
    apiRef,
    entity,
    objectCode,
    actions,
    isNestedView,
    isDropDownWindowOpen,
    getLeaveForm,
    onResetEditForm,
    onSelectPickerOption,
    onMultiSelectPickerOption,
    initialCommonParameters,
    initialColumnVisibilityModel,
    type,
    parentFormObjectId,
    parentFormObjectCode,
    parentFormElementId,
    parentFormObjectIdType,
    fieldId,
    columnId,
    generalFilterField,
    valueId,
    multipleSelectPickerOption,
    objectValueNameOption,
    columns,
    entityRestrictions: entityRestrictions ?? ({} as RestrictionData),
    isLoadingRestrictions,
    handleUpdateRestrictions: updateRestrictions,
    handleDeleteRestrictionsByType: removeRestrictions,
    onSetColumnVisibilityModel: handleSetColumnsVisibilityModel,
    handleOpenForm,
    search,
    isAssistiantSearch,
    dialogId,
    customPageSize,
    viewColumns,
    readonly,
    contextId: context?.id,
    defaultFilter,
    outerRequestData,
    expanded,
    prefillCreateObjectRecordInForm,
    prefillData,
    isCtrlPressed,
    onRowClick,
    onRowSelection,
    onSortChange,
    updateDefaultRestrictions,
    defaultEntityRestrictions,
    outerVariables: outerData?.variables,
    outerEventBus,
    draggableRowImageRef,
  })

  const {
    samePageFormsData,
    currentDynamicForm,
    currentStaticForm,
    selectedRowIds,
    searchAssistantValue,
    isEditMode,
    hasNewRows,
    hasUnsavedChanges,
    hasExternalActionData,
    hasOnRowSelectionEvent,
    hasDragStartEvent,
    activeDownloadRows,
  } = state

  const {
    handleSetSelectedRowIds,
    handleEdit,
    handleClickDelete,
    handleAdditionalButtonClick,
    handleDownloadButtonClick,
    handlePreviewButtonClick,
    handleSetTotalElements,
    handleSetPinnedColumns,
    handleSetColumnNameCheckBox,
    handleSetSortKeysColumnCheckBox,
    handleSetSortKeysForQuickSearch,
    handleSetSearchRule,
    handleSetGlobalFilter,
    refreshObjectData,
    handleRowButtonClick,
    refreshChangeSortData,
    handleSetDataOnVariableChangeEvent,
    setSelectedRows,
  } = handlers

  const {
    staticFormsData,
    dynamicFormsData,
    totalCountElements,
    objectData,
    externalActionData,
    isLoadingObjectData,
    hasWrapHeaderText,
    isEnabledLocalParameters,
    newRows,
    selectedRows,
    unsavedChangesRef,
    hasWrapRowText,
  } = data

  const {
    position,
    onRefContextMenu,
    onClickLink,
    onExecuteCommand,
    onOpenContextMenu,
    onCloseContextMenu,
    onExecuteAction,
  } = useContextMenu({ dialogId })

  //замена дефолтных экшенов на перекрывающие из LC
  const hasRedifinitionActions = hasRedifinitionByDataType(
    outerRequestData,
    REDIFINITION_DATA_TYPE.ACTIONS
  )

  const actionsForConfig = hasRedifinitionActions
    ? outerRequestData?.redifinitions?.[0].actions ?? []
    : entity?.actions ?? []

  const configActions = useGetActions({
    actions: actionsForConfig,
    onClickLink,
    onExecuteCommand,
    onExecuteAction,
  })

  const hasSetDataAction = actionsForConfig.some(
    action => action.actionCode === ACTION_CODE.SET_DATA
  )

  const processedObjectData = useMemo(() => {
    if (externalActionData.length && isListControl) {
      return applyUnsavedChanges(externalActionData, unsavedChangesRef.current)
    }

    if (hasSetDataAction && isListControl) return []

    if (hasNewRows || hasUnsavedChanges) {
      return applyUnsavedChanges([...newRows, ...enrichedObjectData], unsavedChangesRef.current)
    }

    return enrichedObjectData
  }, [enrichedObjectData, externalActionData, newRows, expanded, isListControl, hasSetDataAction])

  const { tooltipPosition, tooltipData, handleOpenTooltip, handleCloseTooltip } = useHandleTooltip({
    configActions,
    columns: viewColumns,
    apiRef,
  })

  const {
    columnsForEntity,
    areAllColumnsHidden,
    setColumnVisibilityModel,
    columnVisibilityModel,
    shouldDisplayCreateButton,
    shouldDisplayNewLineButton,
    rowCreateAction,
    hasRowAction,
    isColumnReorderEnabled,
    columnsExist,
    showColumnsButton,
    globalStyles,
  } = useColumns({
    contextId: context?.id,
    objectData: processedObjectData,
    columns,
    onSetColumns: setColumns,
    entity,
    type,
    readonly,
    selectedRowIds,
    actions: actionsForConfig,
    isViewDialogWindow,
    isListControlDialogWindow,
    handleAdditionalButtonClick,
    handleDownloadButtonClick,
    handlePreviewButtonClick,
    setDropDownWindowOpen,
    handleEdit,
    isEditMode,
    hasNewRows,
    handleRowButtonClick,
    handleClickDelete,
    parentFormIsDirty,
    handleOpenForm,
    viewColumns,
    hasQuickSearch,
    setInitialColumnVisibilityModel,
    entityRestrictions,
    handleSetPinnedColumns,
    handleOpenTooltip,
    handleCloseTooltip,
    apiRef,
    hasWrapHeaderText,
    isEnabledLocalParameters,
    isCtrlPressed,
    dialogId,
    hasRedifinitionActions,
    unsavedChangesRef,
    hasExternalActionData,
    hasOnRowSelectionEvent,
    hasWrapRowText,
    defaultEntityRestrictions,
    hasDragStartEvent,
    globalColumnHeaderStyles,
    globalColumnTextStyles,
    activeDownloadRows,
    instanceId,
  })

  useSetObjectDataOnVariableChange(outerRequestData, handleSetDataOnVariableChangeEvent)

  const getActionsForChangeVariableActions = (type: ENTITY) => {
    switch (type) {
      case ENTITY.LIST_CONTROL: {
        return outerRequestData?.redifinitions?.[0]?.actions
      }
      case ENTITY.VIEW: {
        return actions
      }
      default: {
        return actions
      }
    }
  }

  useChangeVariableActions({
    actions: getActionsForChangeVariableActions(type),
    objectData,
    selectedRows,
    hasExternalActionData,
    entityId: entity?.id,
    outerVariables: outerData?.variables,
    outerEventBus,
    type,
  })

  const { modifyData } = useModifyDataAfterLoad()

  const actionsObject = useMemo(() => {
    const list = entity?.actions ?? []

    return list.reduce((acc, action) => {
      acc[action.eventCode as EVENT_CODE] = action

      return acc
    }, {} as Record<EVENT_CODE, ActionRow>)
  }, [entity?.actions])

  const handleResetEntity = () => {
    onSetEntity(null)
    handleSetColumnNameCheckBox({})
    handleSetSortKeysColumnCheckBox({})
    handleSetSortKeysForQuickSearch([])
    handleSetSearchRule(
      {
        name: SEARCH_RULE_TYPE.CONTAIN,
        exception: SEARCH_RULE_EXCEPTION[SEARCH_RULE_TYPE.CONTAIN],
      },
      true
    )
    handleSetGlobalFilter('')
  }

  useEffect(() => {
    const enrichData = async () => {
      if (!objectData.length || isLoadingObjectData) {
        setEnrichedObjectData(objectData)

        return
      }

      try {
        const result = await modifyData(actionsObject, objectData)
        setEnrichedObjectData(result)
      } catch (error) {
        console.error(error)
        setEnrichedObjectData(objectData)
      }
    }

    enrichData()
  }, [objectData, isLoadingObjectData])

  const isDragActive = useHandleColumnDrag(
    apiRef,
    entityId as string,
    entityRestrictions,
    entity?.columns || [],
    columnsForEntity,
    updateRestrictions
  )

  useEffect(() => {
    setVisibleColumns(viewColumns)
  }, [viewColumns])

  useEffect(() => {
    if (totalCountElements === 0) {
      handleSetTotalElements(0)
    }
    if (totalCountElements) {
      handleSetTotalElements(totalCountElements)
    }
  }, [totalCountElements])

  useEffect(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) return

    if (multipleSelectPickerOption) {
      const selectedIds = multipleSelectPickerOption.map(opt => String(opt.id))

      const matchedRows = objectData.filter(row => {
        return selectedIds.some(id => id === row._id || id === row.internal_id)
      })

      setSelectedRows(matchedRows)
    } else {
      const matchedRow = objectData.find(
        row =>
          selectedPickerOptionValue?.id === row._id ||
          selectedPickerOptionValue?.id === row.internal_id
      )

      if (matchedRow) {
        setSelectedRows([matchedRow])
      }
    }
  }, [multipleSelectPickerOption, objectData, selectedPickerOptionValue, type])

  useEffect(() => {
    const currentStaticFormData = staticFormsData[currentStaticForm]
    const currentDynamicFormData = dynamicFormsData[currentDynamicForm]
    const lastSamePageFormVariant = samePageFormsData.at(-1)?.variant

    if (!lastSamePageFormVariant) {
      return
    }

    if (lastSamePageFormVariant === PASSED_FORM_DATA_VARIANT.DYNAMIC && currentDynamicFormData) {
      handleSetSelectedRowIds(currentDynamicFormData.rowId as string)
    }

    if (lastSamePageFormVariant === PASSED_FORM_DATA_VARIANT.STATIC && currentStaticFormData) {
      handleSetSelectedRowIds(currentStaticFormData.rowId as string)
    }
  }, [currentStaticForm, currentDynamicForm, samePageFormsData])

  const { contextMenuData, handleContextMenu } = useContextMenuHandlers(
    columns,
    configActions,
    apiRef,
    onOpenContextMenu,
    { outerSrcObj: outerRequestData?.srcObj },
    outerData?.variables
  )

  // TODO В коммите написано WF-1690 - Навигация в списочных представлениях с помощью клавиш
  // TODO Но непонятно, как это работает
  // TODO При обновлении с 5 на 6 версию, необходимо избавиться от такого события и использовать slotProps.cell.onFocus
  // useEffect(() => {
  //   if (entity && !isLoadingView && apiRef.current && apiRef.current.subscribeEvent) {
  //     apiRef.current.subscribeEvent('cellFocusIn', params => {
  //       console.log('handleSetSelectedRowIds cellFocusIn')
  //       handleSetSelectedRowIds(params.row._id)
  //     })
  //   }
  // }, [apiRef])

  const isForm = pathname.includes('/forms/')
  const isSearchResult = isAssistiantSearch && entityId
  const isShowTableData = Boolean(entityId)

  const handleResetRestrictions = () => {
    handleSetColumnNameCheckBox({})
    handleSetSortKeysColumnCheckBox({})
    handleSetSortKeysForQuickSearch([])
    removeAllRestrictions().then(({ sort }) => {
      if (sort) {
        // Обновление сортировки вручную, для обновления данных по новой сортировке
        refreshChangeSortData(
          sort
            .filter(sortItem =>
              viewColumns.some(col => col.code === sortItem.fieldCode && col.visibility)
            )
            .map(sortItem => ({
              field: sortItem.fieldCode,
              sortKey: sortItem.value.columnToSort,
              priority: sortItem.value.priority,
              sort: sortItem.value.sortDirection,
            }))
            ?.sort((sortA, sortB) => sortA.priority - sortB.priority)
        )
      }
    })
  }

  return {
    state: {
      ...state,
      globalStyles,
      isListControl,
      columnVisibilityModel,
      visibleColumns,
      viewColumns,
      entity,
      configActions,
      contextMenuData,
      onRefContextMenu,
      position,
      tooltipPosition,
      tooltipData,
      isLoadingRestrictions: isLoadingRestrictions || isLoadingDefaultRestrictions,
      isPrefillDataLoading,
      isCtrlPressed,
      isDropDownWindowOpen,
    },
    data: {
      ...data,
      areAllColumnsHidden,
      showColumnsButton,
      isAssistiantSearch,
      objectData: areAllColumnsHidden ? [] : processedObjectData,
      objects,
      actions,
      objectCode,
      columns: columnsForEntity,
      isForm,
      isSearchResult,
      isShowTableData,
      rowCreateAction,
      hasRowAction,
      shouldDisplayCreateButton,
      shouldDisplayNewLineButton,
      apiRef,
      isColumnReorderEnabled,
      hasQuickSearch,
      columnsExist,
      isDragActive,
    },
    handlers: {
      onCloseContextMenu,
      handleContextMenu,
      handleOpenTooltip,
      handleCloseTooltip,
      handleResetRestrictions,
      ...handlers,
    },
  }
}
